import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Paper,
  Container,
  Pagination,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  IconButton,
} from "@mui/material";
import { AllTypoFontStyles } from "../../../../Styles/styles";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useSearchEventsMutation } from "../../../../Redux/apis/ApiSlice";
import { useNavigate } from "react-router-dom";
import AdjustIcon from "@mui/icons-material/Adjust";
import moment from "moment";
import eventCardDefaultIMG from "../../../../Images/EventCardDefaultIMG.jpg";
import { ClearIcon, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { RecenterMap, userIcon } from "../EventsPage/EventsPage";
import { DatePicker, QRCode } from "antd";
// Set your Mapbox access token here
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const ZoomToLocation = ({ position }) => {
  const map = useMap();
  React.useEffect(() => {
    if (position) {
      map.setView(position, 12, { animate: true }); // Adjust zoom level (e.g., 12 for a deep zoom)
    }
  }, [position, map]);
  return null;
};

const FlyToLocation = ({ position }) => {
  const map = useMap();

  React.useEffect(() => {
    if (position) {
      map.flyTo(position, 14, {
        duration: 2, // Smooth transition duration
        easeLinearity: 0.25, // Easing effect for smoother zoom
      });
    }
  }, [map, position]);

  return null;
};

const ExploreSearch = ({ filters, onFilterChange }) => {
  const [userLocation, setUserLocation] = useState([47.751076, -120.740135]);
  const [totalCount, setTotalCount] = useState(0);
  const fgColor = "#000000";
  const [selectedPosition, setSelectedPosition] = React.useState(null);
  const [eventName, setEventName] = useState(filters.eventName);
  const [dateRange, setDateRange] = useState(filters.dateRange);
  const [showMap, setShowMap] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 3;
  const navigate = useNavigate();
  const locationInputRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(
    filters.location || ""
  );

  const handleChangePage = (event, value) => {
    setPage(value);
  };
  // Refs for Mapbox elements
  const geocoderContainerRef = useRef(null);
  const [searchEvents, { data: eventsData, error, isLoading }] =
    useSearchEventsMutation();
  // Handler functions for inputs
  const handleEventNameChange = (e) => {
    const newEventName = e.target.value;
    setEventName(newEventName);
    onFilterChange({ ...filters, eventName: newEventName });
  };

  const formatToUTC = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setUTCHours(24, 0, 0, 0);
    return d.toISOString();
  };

  // Start Date Update Function
  const updateStartDate = (startDate) => {
    const startIso = formatToUTC(startDate);
    setDateRange((prev) => [startIso, prev?.[1] || null]);
    onFilterChange({
      ...filters,
      dateRange: [startIso, filters.dateRange?.[1] || null],
    });
  };

  // End Date Update Function
  const updateEndDate = (endDate) => {
    const endIso = formatToUTC(endDate);
    setDateRange((prev) => [prev?.[0] || null, endIso]);
    onFilterChange({
      ...filters,
      dateRange: [filters.dateRange?.[0] || null, endIso],
    });
  };

  // Main Function
  const handleDateRangeChange = (dates) => {
    updateStartDate(dates?.[0]);
    updateEndDate(dates?.[1]);
  };

  useEffect(() => {
    if (!geocoderContainerRef.current) return;

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: "Enter Location",
    });

    geocoder.on("result", (event) => {
      const { place_name } = event.result;
      onFilterChange({ ...filters, location: place_name });
      if (locationInputRef.current) {
        locationInputRef.current.value = place_name; // Set the value directly
      }
    });

    const geocoderElement = geocoder.onAdd();
    geocoderContainerRef.current.appendChild(geocoderElement);

    return () => {
      if (geocoderContainerRef.current) {
        geocoderContainerRef.current.removeChild(geocoderElement);
      }
    };
  }, [filters, onFilterChange]);
  const truncateText = (text, maxWords) => {
    const words = text?.split(" ");
    return words.length > maxWords
      ? words.slice(0, maxWords).join(" ") + "..."
      : text;
  };
  // const paginatedEvents = eventsData?.events.slice(
  //   (page - 1) * itemsPerPage,
  //   page * itemsPerPage
  // );
  const paginatedEvents = eventsData?.events;

  const handleCardClick = (event) => {
    // console.log("popopopopop2", event);
    navigate(`/event-detailDesc/${event.eventId}`, { state: { event } });
  };
  const handleSearch = () => {
    searchEvents({
      eventName: filters?.eventName,
      location: filters?.location,
      dateRange: filters?.dateRange,
      status: filters.status,
      page,
      limit: itemsPerPage,
    }).then((res) => {
      if (res?.data?.totalCount) {
        setTotalCount(res.data.totalCount);
      }
    });
  };

  // Call search when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    handleSearch();
  }, [filters, page]);

  return (
    <div style={{ padding: "16px" }}>
      {/* First row with 4 and 8 columns */}
      <Grid container spacing={3} sx={{ height: "100%" }}>
        {/* Left Panel */}
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Paper elevation={3} sx={{ p: 3, flexGrow: 1 }}>
            <Box mb={3}>
              <Typography sx={AllTypoFontStyles.CommonTypo2} gutterBottom>
                Event Name
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Event Name"
                value={eventName}
                onChange={handleEventNameChange}
              />
            </Box>

            <Box mb={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column" },
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  "& > div": { width: "100%" },
                }}
              >
                {/* Start Date Section */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Typography sx={AllTypoFontStyles.CommonTypo2} gutterBottom>
                    Start Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      fullWidth
                      variant="outlined"
                      size="large"
                      placeholder="Start Date"
                      clearable
                      clearText="Clear"
                      format={"MM/DD/YYYY"}
                      onChange={(newValue) => updateStartDate(newValue)}
                      slotProps={{
                        textField: {
                          size: "large",
                          inputProps: { readOnly: true },
                        },
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Box>

                {/* End Date Section */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Typography sx={AllTypoFontStyles.CommonTypo2} gutterBottom>
                    End Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      fullWidth
                      variant="outlined"
                      size="large"
                      placeholder="End Date"
                      format={"MM/DD/YYYY"}
                      slotProps={{
                        textField: {
                          size: "large",
                          inputProps: { readOnly: true },
                        },
                      }}
                      onChange={(newValue) => updateEndDate(newValue)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Box>

            <Box mb={3}>
              <Typography sx={AllTypoFontStyles.CommonTypo2} gutterBottom>
                Location
              </Typography>
              <TextField
                ref={locationInputRef}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter Location"
                value={filters.location || ""}
                onChange={(e) =>
                  onFilterChange({ ...filters, location: e.target.value })
                }
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel (Map) */}
        <Grid
          item
          xs={12}
          md={7}
          lg={8}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          {/* Map Container */}
          <Box
            sx={{
              flexGrow: 1,
              height: "100%",
              overflow: "hidden", // Keep hidden only for map
              visibility: { xs: "hidden", sm: "visible", md: "visible" },
            }}
          >
            <MapContainer
              center={userLocation}
              zoom={userLocation ? 20 : 2}
              style={{
                height: "100%",
                width: "100%",
              }}
            >
              {userLocation && <RecenterMap userLocation={userLocation} />}
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {userLocation && (
                <>
                  <RecenterMap userLocation={userLocation} />
                  <Marker position={userLocation} icon={userIcon}>
                    <Popup>
                      <Typography variant="h6">Your Location</Typography>
                    </Popup>
                  </Marker>
                </>
              )}
              {eventsData?.events?.map((event) => (
                <Marker
                  key={event.eventId}
                  position={[event.latitude, event.longitude]}
                >
                  <Popup>
                    <Typography variant="h6">{event.eventName}</Typography>
                    <Typography variant="body2">{event.location}</Typography>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>

          {!showMap && (
            <Box sx={{ flexGrow: 1, minHeight: "300px" }}>
              {" "}
              {/* Adjusts height dynamically */}
              <MapContainer
                center={[39.6246, 19.9217]}
                zoom={2}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {eventsData?.events.map((event) => (
                  <Marker
                    key={event.id}
                    position={[event.latitude, event.longitude]}
                    eventHandlers={{
                      click: () =>
                        setSelectedPosition([event.latitude, event.longitude]),
                    }}
                  >
                    <Popup>
                      <Typography variant="h6">{event.name}</Typography>
                      <Typography variant="body2">{event.location}</Typography>
                    </Popup>
                  </Marker>
                ))}
                {selectedPosition && (
                  <FlyToLocation position={selectedPosition} />
                )}
              </MapContainer>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Second row with full 12 columns */}
      <Grid container spacing={3} style={{ marginTop: "16px" }}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: { sx: "6px", md: "24px" } }}>
            <Container maxWidth="lg">
              <Grid
                container
                spacing={3}
                sx={{ justifyContent: "center", display: "flex" }}
              >
                {isLoading ? (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <GradientCircularProgress />
                  </Grid>
                ) : paginatedEvents && paginatedEvents?.length > 0 ? (
                  paginatedEvents?.map((event, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Card
                        sx={{
                          borderRadius: "10px",
                          height: "100%",
                          padding: "0px, 16px, 0px, 16px",
                          gap: 10,
                          cursor: "pointer",
                        }}
                        onClick={() => handleCardClick(event)}
                      >
                        {event.eventPhoto?.length > 0 ? (
                          <CardMedia
                            component="img"
                            height="300"
                            image={event.eventPhoto[0]}
                            alt={event.eventName}
                            sx={{ borderRadius: "8px" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 250,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f9f9f9",
                              borderRadius: "8px",
                            }}
                          >
                            <QRCode
                              value={`https://gxtickets.com/detail/${event.eventId}`}
                              size={150}
                              level="H"
                              fgColor={fgColor}
                              bgColor="#ffffff"
                              includeMargin
                            />
                          </Box>
                        )}
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontSize: "12px",
                              }}
                            >
                              Event Date:{" "}
                              {moment(event.startDateTime).format("MM/DD/YYYY")}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                columnGap: "5px",
                              }}
                            >
                              <AdjustIcon
                                sx={{ fontSize: "14px", color: "black" }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: 400,
                                  fontSize: "12px",
                                }}
                              >
                                {event?.ticket?.[0]?.subTickets?.every(
                                  (sub) => sub.quantity === 0
                                )
                                  ? "SOLD OUT"
                                  : event?.status?.toUpperCase()}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography
                            sx={{
                              mt: 1,
                              fontWeight: 700,
                              fontSize: "20px",
                            }}
                          >
                            {/* {event.eventName} */}
                            {event?.eventName
                              ? event.eventName.charAt(0).toUpperCase() +
                                event.eventName.slice(1)
                              : ""}
                          </Typography>
                          {/* <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          > */}
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: "14px",
                            }}
                          >
                            {/* Location: {truncateText(event.location, 2)} */}
                            Location: {truncateText(event.location, 7)}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: "14px",
                            }}
                          >
                            Organization: {event.organizationName}
                          </Typography>
                          {/* </Box> */}
                          {/* <Typography
                            sx={{
                              mt: 1,
                             
                              fontWeight: 400,
                              fontSize: "12px",
                            }}
                          >
                            {truncateText(event.description, 4)}
                          </Typography> */}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="h6" align="center">
                      No events available1
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Container>
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination
                count={Math.ceil(
                  (eventsData?.totalCount  || 0) / itemsPerPage
                )}
                page={page}
                onChange={handleChangePage}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default ExploreSearch;

function GradientCircularProgress() {
  return (
    <React.Fragment>
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e01cd5" />
            <stop offset="100%" stopColor="#1CB5E0" />
          </linearGradient>
        </defs>
      </svg>
      <CircularProgress
        size={80}
        sx={{ "svg circle": { stroke: "url(#my_gradient)" } }}
      />
    </React.Fragment>
  );
}
